import type { AgentState, AgentView, AppId, PresentationMode } from "@walkie/protocol"

/**
 * Mirrors AGENT_HUES in @walkie/protocol. Copied (not imported) because the
 * protocol module pulls zod in at runtime and the web app uses it for types only.
 */
export const AGENT_HUES = [24, 200, 140, 280, 340, 50, 170, 230, 310, 90, 0, 260]

export function agentHue(color: number): number {
	const n = AGENT_HUES.length
	return AGENT_HUES[((color % n) + n) % n] ?? 24
}

export const agentColor = (color: number) => `hsl(${agentHue(color)} 80% 62%)`
export const agentTint = (color: number, a = 0.14) =>
	`hsl(${agentHue(color)} 80% 62% / ${a})`

/**
 * One or two characters per agent. When several agents share a first letter
 * (ada-cx1, ada-cx2; nova, nova-2) each gets a second character from the end of
 * its handle, so the swatch alone still tells them apart.
 */
export function glyphMap(agents: AgentView[]): Map<string, string> {
	const out = new Map<string, string>()
	const groups = new Map<string, AgentView[]>()
	const first = (a: AgentView) => (a.handle.match(/[a-z0-9]/i)?.[0] ?? "?").toUpperCase()
	for (const a of agents) groups.set(first(a), [...(groups.get(first(a)) ?? []), a])
	for (const [f, group] of groups) {
		if (group.length === 1) {
			out.set(group[0]!.id, f)
			continue
		}
		const sorted = [...group].sort((x, y) => x.createdAt.localeCompare(y.createdAt))
		const used = new Set<string>()
		sorted.forEach((a, i) => {
			const tail = a.handle.includes("-") ? (a.handle.split("-").pop() ?? "") : ""
			let g = tail ? f + tail[tail.length - 1]!.toLowerCase() : f
			if (used.has(g)) g = f + (a.handle[1] ?? String(i + 1)).toLowerCase()
			if (used.has(g)) g = f + String(i + 1)
			used.add(g)
			out.set(a.id, g)
		})
	}
	return out
}

export const APP_LABEL: Record<AppId, string> = {
	"claude-code": "Claude Code",
	"claude-desktop": "Claude Desktop",
	"codex-cli": "Codex CLI",
	"codex-desktop": "Codex Desktop",
	"codex-ide": "Codex IDE",
	cursor: "Cursor",
	other: "Other",
}

export const APP_SHORT: Record<AppId, string> = {
	"claude-code": "CC",
	"claude-desktop": "CD",
	"codex-cli": "CX",
	"codex-desktop": "CXd",
	"codex-ide": "CXi",
	cursor: "CU",
	other: "··",
}

export const STATE_LABEL: Record<AgentState, string> = {
	active: "active",
	idle: "idle",
	offline: "offline",
	ended: "ended",
}

export const WAKE_INFO: Record<
	PresentationMode,
	{ label: string; warn: boolean; tip: string }
> = {
	"hook-rewake": {
		label: "wakes",
		warn: false,
		tip: "Wakes when idle: a background hook starts a new turn with the message.",
	},
	"app-server": {
		label: "wakes",
		warn: false,
		tip: "Wakes when idle through the Codex app-server this connector owns.",
	},
	"hook-context": {
		label: "next turn",
		warn: true,
		tip: "Next turn only: messages are added to its context on its next prompt or tool call. An idle chat cannot be woken from outside (platform limit).",
	},
	"tool-only": {
		label: "tool-only",
		warn: true,
		tip: "Sees messages only when it calls a Walkie tool. It cannot be woken (Claude Desktop limitation).",
	},
}

/** One icon per delivery capability. Always shown with a tooltip and an aria-label. */
export const WAKE_ICON: Record<PresentationMode, { icon: string; name: string }> = {
	"hook-rewake": { icon: "\u26A1\uFE0E", name: "Wakes when idle" },
	"app-server": { icon: "\u26A1\uFE0E", name: "Wakes when idle" },
	"hook-context": { icon: "\u23ED\uFE0E", name: "Next turn only" },
	"tool-only": { icon: "\u{1F527}\uFE0E", name: "Tool-only" },
}
export const AUTO_RESUME_ICON = { icon: "\u27F3", name: "Auto-resume on" }

export const STATE_RANK: Record<AgentState, number> = {
	active: 0,
	idle: 1,
	offline: 2,
	ended: 3,
}

/** Mirrors HANDLE_PATTERN in @walkie/protocol. */
export const HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,31}$/
